import UUID from 'uuid';
import qs from 'qs';
import type { AnalyticsQueryParams } from './index';
import type { UserContext } from '../schemas/analytics';
import { setSession, getSession, clearDwellSession } from './analytics-cookie';
import window from '../window_wrapper';

const THIRTY_MINUTES_IN_MILLIS = 1800000;

function removeAnchor(url: string) {
  if (!url) { return ''; }

  return url.split('#')[0]; // remove anchor
}

/**
 * The analytics session, shared with the server through the elog-analytics-2
 * cookie ({ id, sessionID, lastSeen }).
 *
 * The server owns minting. When a page navigation arrives with an expired
 * session, Middleware::ClientContextID (reverb repo) rotates the cookie before
 * rendering, so every event in that request already carries the new id.
 *
 * This class keeps the session alive: it adopts the id the cookie holds while
 * the session is live and bumps lastSeen on construction and on every page
 * view. It mints only when the cookie holds no live session, which happens on
 * pages that never reach Rails (CDN cache hits) and on first visits before any
 * cookie exists.
 */
export class Session {
  _sessionID: string;

  _pageViewID: string;

  _lastPageVisited: Partial<Location>;

  _currentReferer: string;

  _location: Partial<Location>;

  _userContext: Partial<UserContext>;

  constructor(userContext, loc: Partial<Location> = window.location) {
    this._location = { ...loc };
    this._userContext = userContext;
    this._sessionID = this.touchSession();
  }

  touch(loc: Partial<Location> = window.location, doc: Partial<Document> = document) {
    if (this._lastPageVisited) {
      this._lastPageVisited = { ...this._location };
    } else {
      this._lastPageVisited = { href: doc.referrer };
    }

    this._location = { ...loc };
    this._pageViewID = UUID.v4();
    this._sessionID = this.touchSession();

    return this._sessionID;
  }

  touchSession() {
    const now = new Date().getTime();
    const { lastSeen, sessionID } = getSession();

    if (!sessionID || !lastSeen) {
      return this.start(now);
    }

    const age = now - lastSeen;
    if (age > THIRTY_MINUTES_IN_MILLIS) {
      return this.start(now);
    }

    this.setSessionID(sessionID, now);
    return this._sessionID;
  }

  start(now) {
    const id = UUID.v4();
    clearDwellSession();
    this.setSessionID(id, now);

    return id;
  }

  setSessionID(sessionID, now) {
    this._sessionID = sessionID;
    this._userContext.session_id = sessionID;
    setSession(sessionID, now);
  }

  setRequestID(requestID: string) {
    this._userContext.request_id = requestID;
  }

  requestId() {
    return this._userContext.request_id;
  }

  cookieId() {
    return this._userContext.cookie_id;
  }

  deviceName() {
    return this._userContext.device?.name;
  }

  url() {
    return removeAnchor(this._location.href);
  }

  query() {
    return qs.parse(this._location.search, { ignoreQueryPrefix: true }) as AnalyticsQueryParams;
  }

  ID() {
    return this._sessionID;
  }

  referer(doc: Partial<Document> = document) {
    if (!this._lastPageVisited) {
      return removeAnchor(doc.referrer);
    }

    return removeAnchor(this._lastPageVisited.href);
  }

  pageViewID() {
    return this._pageViewID;
  }
}
